import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Building2, X } from 'lucide-react';
import { Property } from '../types';
import { supabase } from '../lib/supabase';

interface SearchAutocompleteProps {
  value: string;
  onChange: (query: string) => void;
  onSelect?: (property: Property) => void;
  placeholder?: string;
  className?: string;
  showSuggestions?: boolean;
  maxSuggestions?: number;
}

interface SearchSuggestion {
  id: string | number;
  title: string;
  location: string;
  developer: string;
  price: string;
  matchField: 'title' | 'location' | 'developer' | 'description';
  matchText: string;
}

export const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search projects, areas, developers...",
  className = "",
  showSuggestions = true,
  maxSuggestions = 8
}) => {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions from database
  useEffect(() => {
    if (!value || value.trim().length < 2 || !showSuggestions) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const searchQuery = value.trim();
    setIsLoading(true);
    setIsOpen(true);

    const fetchSuggestions = async () => {
      try {
        // Escape special characters for Postgres ILIKE
        const escapedQuery = searchQuery
          .replace(/\\/g, '\\\\')
          .replace(/%/g, '\\%')
          .replace(/_/g, '\\_')
          .replace(/\[/g, '\\[');
        
        // Search across title, location_display, developer, district, description
        const query = supabase
          .from('projects_canonical')
          .select('id, title, location_display, developer, price_rounded_aed, district, description')
          .or(`title.ilike.%${escapedQuery}%,location_display.ilike.%${escapedQuery}%,developer.ilike.%${escapedQuery}%,district.ilike.%${escapedQuery}%,description.ilike.%${escapedQuery}%`)
          .limit(maxSuggestions * 2); // Fetch more to filter and rank

        const { data, error } = await query;

        if (error) {
          console.error('Search error:', error);
          setSuggestions([]);
          return;
        }

        if (!data || data.length === 0) {
          setSuggestions([]);
          return;
        }

        // Rank and format suggestions
        const ranked: (SearchSuggestion & { score: number })[] = data.map((project: any) => {
          const searchLower = searchQuery.toLowerCase();
          const titleLower = (project.title || '').toLowerCase();
          const locationLower = (project.location_display || '').toLowerCase();
          const developerLower = (project.developer || '').toLowerCase();
          const districtLower = (project.district || '').toLowerCase();
          const descLower = (project.description || '').toLowerCase();

          let matchField: 'title' | 'location' | 'developer' | 'description' = 'title';
          let matchText = project.title || '';
          let score = 0;

          // Prioritize matches: title > location > developer > description
          if (titleLower.includes(searchLower)) {
            matchField = 'title';
            matchText = project.title;
            score = 100;
          } else if (locationLower.includes(searchLower) || districtLower.includes(searchLower)) {
            matchField = 'location';
            matchText = project.location_display || project.district || '';
            score = 80;
          } else if (developerLower.includes(searchLower)) {
            matchField = 'developer';
            matchText = project.developer || '';
            score = 60;
          } else if (descLower.includes(searchLower)) {
            matchField = 'description';
            matchText = (project.description || '').substring(0, 100);
            score = 40;
          }

          return {
            id: project.id,
            title: project.title || 'Untitled',
            location: project.location_display || project.district || 'Dubai',
            developer: project.developer || 'Unknown',
            price: project.price_rounded_aed 
              ? `AED ${Math.floor(project.price_rounded_aed).toLocaleString()}`
              : 'Price on Request',
            matchField,
            matchText,
            score
          };
        });

        // Sort by score (highest first) and limit
        ranked.sort((a, b) => b.score - a.score);
        setSuggestions(ranked.slice(0, maxSuggestions).map(({ score, ...rest }) => rest));

      } catch (err) {
        console.error('Search failed:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [value, showSuggestions, maxSuggestions]);

  const handleSelect = (suggestion: SearchSuggestion) => {
    // Create a Property-like object from the suggestion
    const property: Property = {
      id: suggestion.id,
      title: suggestion.title,
      location: suggestion.location,
      developer: suggestion.developer,
      price: suggestion.price,
      priceRaw: 0,
      priceNumeric: 0,
      pricePerSqft: 0,
      sqftNumeric: 0,
      roi: 'N/A',
      roiNumeric: 0,
      roiTrend: 'flat',
      completion: 'TBD',
      image: '',
      developerLogo: '',
      createdAt: new Date().toISOString(),
      badge: { text: 'OFF-PLAN', color: 'blue' }
    };

    onChange(suggestion.title); // Update search input
    setIsOpen(false);
    if (onSelect) {
      onSelect(property);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-50 border-2 border-slate-200 text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500/30 focus:bg-white focus:border-blue-500 transition-all shadow-sm"
        />
        {value && (
          <button
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && showSuggestions && value.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              Searching...
            </div>
          ) : suggestions.length > 0 ? (
            <div className="p-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSelect(suggestion)}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {suggestion.title}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {suggestion.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 size={12} />
                          {suggestion.developer}
                        </span>
                      </div>
                      {suggestion.matchField !== 'title' && (
                        <div className="mt-1 text-xs text-slate-400">
                          Matched in {suggestion.matchField}: {suggestion.matchText.substring(0, 60)}...
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-slate-700 shrink-0">
                      {suggestion.price}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No projects found matching "{value}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};
